import { UserSubscriptions } from 'subscription/subscriptionState'
import { some, none, Option, AsyncState} from 'widgets-for-react'

// Persoonlijk identificeerbare informatie
export interface PIIData {
  userID: string,
  email: string,
  name: string,
}

export interface EcommerceData {
  transactionId: string;
  value: number;
}

export const initializePIIData = (userSubscriptionState: AsyncState<UserSubscriptions>): PIIData | undefined => {

  if(userSubscriptionState.kind !== 'loaded') {
    return;
  }

  const user = userSubscriptionState.value.User;

  return { userID: user.Id.toString(), email: user.Email, name: user.Name }
}

export namespace DataLayer {
  // ===================
  // | Free trial form |
  // ===================

  export const changeTrialFormValue = (
    fieldLabel: string,
    fieldValue?: string
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'proefperiode flow',
      action: fieldLabel,
      label: fieldValue,
    })

  export const errorOnTrialForm = (error: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'proefperiode flow',
      action: 'proefperiode formulier error',
      label: error,
    })
  
  export const startTrialFlow = (piiData: PIIData) => {
    DatalayerApi.push({
      event: "add_to_cart",
      category: "proefperiode flow",
      action: "success",
      label: "",
      user: piiData, 
    })
  }

  export const successOnTrialForm = (accountType?: 'student' | 'teacher') =>
    DatalayerApi.push({
      event: 'interaction',
      category: accountType ? `${accountType} flow` : 'proefperiode flow',
      action: 'success',
      label: '',
    })

  export const confirmTrial = (accountType?: 'student' | 'teacher') =>
    DatalayerApi.push({
      event: 'interaction',
      category: accountType ? `${accountType} flow` : 'proefperiode flow',
      action: 'proefperiode gestart',
      label: '',
    })

  export const newTrialAccountCreated = (userID: string) => {
    DatalayerApi.push({
      event: "proefperiode_account_aangemaakt",
      category: "proefperiode flow",
      action: "success",
      label: "",
      userID,     
    })
  }

  // ================
  // | Upgrade flow |
  // ================

  export const initiateCheckOut = (piiData?: PIIData) => {
    DatalayerApi.push({
      event: "initiate_checkout",
      category: "proefperiode flow",
      action: "success",
      label: "",
      user: piiData    
    })
  }

  export const upgradeFlowStepError = (step: number, error: string) => {
    DatalayerApi.push({
      event: 'interaction',
      category: 'upgrade flow',
      action: `stap ${step} - error`,
      label: error,
    })
  }

  export const upgradeFlowChangeStep = (
    currentStep: number,
    buttonText: string,
    action: 'next' | 'back'
  ) => {
    DatalayerApi.push({
      event: 'interaction',
      category: 'upgrade flow',
      action: `stap ${currentStep}`,
      label:
        action == 'next'
          ? buttonText
          : `${buttonText} naar stap ${currentStep - 1}`,
    })
  }

  export const upgradeFlowStep1 = (pageload: boolean,  piiData?: PIIData, frequency?: string) => {

    if(pageload) {
      return;
    }

    let translatedFrequency = '';

    if (frequency) {
      if (frequency.includes('Monthly')) 
      {
        translatedFrequency = 'maandelijks';
      } 
      else if (frequency.includes('Yearly')) 
      {
        translatedFrequency = 'jaarlijks';
      }
    }
    
    const price: Record<string, number> = {
      'maandelijks': 9.99,
      'jaarlijks': 99,
    };

    DatalayerApi.push({
      event: 'interaction',
      category: 'upgrade flow',
      action: 'stap 1',
      label: translatedFrequency,
      user: piiData,
      ecommerce: {
        currency: 'EUR',
        value: price[translatedFrequency],
        items: [{
          itemId: `Pro Account ${translatedFrequency}`,
          itemName: `Pro account ${translatedFrequency}`,
          quantity: 1,
          price: price[translatedFrequency]
      }]}
    })
  }

  export const upgradeFlowStep2 = (field: string, piiData?: PIIData, value?: string) => {
    DatalayerApi.push({
      event: 'interaction',
      category: 'upgrade flow',
      action: `stap 2 - ${field}`,
      label: value,
      user: piiData
    })
  }

  export const upgradeFlowStep3 = (field: string, piiData?: PIIData, value?: string) => {
    DatalayerApi.push({
      event: 'interaction',
      category: 'upgrade flow',
      action: `stap 3 - ${field}`,
      label: value,
      user: piiData
    })
  }

  export const upgradeFlowSuccessfulPayment = (paymentFrequency: string, piiData?: PIIData, ecommerceData?: EcommerceData) => {

    paymentFrequency = paymentFrequency.toLowerCase() === 'monthly' ? 'monthly' : 'yearly';

    const price: Record<string, number> = {
      'monthly': 9.99,
      'yearly': 99,
    };

    DatalayerApi.push({
      event: 'noninteraction',
      category: 'upgrade flow',
      action: 'stap 4',
      label: `succesvolle upgrade naar ${paymentFrequency} pro-account`,
      user: piiData,
      ecommerce: {...ecommerceData, 
        currency: 'EUR',
        items: [{
          item_name: `Pro Account ${paymentFrequency}`,
          item_id: `pro_${paymentFrequency}`,
          price: price[paymentFrequency],
          quantity: 1,
        }]
      }
    })
  }

  // =========
  // | Login |
  // =========

  export const changeLoginFormValue = (fieldLabel: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'Account',
      action: 'inloggen',
      label: fieldLabel,
    })

  export const clickLoginButton = (buttonText: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'Account',
      action: 'inloggen',
      label: `${buttonText} button`,
    })

  export const errorOnLoginForm = (error: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'Account',
      action: 'inloggen',
      label: `login error - ${error}`,
    })

  export const successfulLogin = (userID: string) =>
    DatalayerApi.push({
      event: 'noninteraction',
      category: 'Account',
      action: 'Login',
      label: userID,
    })

  export const loggedInUserAccountType = (accountType: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'Account',
      action: 'inloggen',
      label: accountType,
    })

  export const loggedInUserJobTitle = (
    accountType: string,
    userID: string,
    jobTitle: string
  ) =>
    DatalayerApi.push({
      event: 'loginType',
      loginType: accountType,
      userID,
      'job title': jobTitle,
    })

  export const succesfulAccountMigration = () =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'Account',
      action: 'inloggen',
      label: 'migratie account succesvol',
    })

  export const succesfulPasswordChange = () =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'Account',
      action: 'inloggen',
      label: 'nieuw paswoord is opgeslagen',
    })

  // ===========
  // | Paywall |
  // ===========

  export const showPaywall = (action: string) =>
    DatalayerApi.push({
      event: 'noninteraction',
      category: 'paywall',
      action: action,
      label: '',
    })

  // ==============
  // | Every page |
  // ==============

  export const headerButtonsClick = (action: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'button clicks',
      action: action,
      label: '',
    })

  export const pageView = () =>
    DatalayerApi.push({
      event: 'virtualPageview',
      pagepath: window.location.pathname,
      pageUrl: window.location.href,
      pageTitle: 'Gastronomixs',
    })

  export const pageNotFound = () =>
    DatalayerApi.push({
      event: 'pageNotFound',
      pagepath: window.location.pathname,
      pageUrl: window.location.href,
      pageTitle: 'Gastronomixs',
    })

  // ===============
  // | Componenten |
  // ===============

  export const startSaveToCollection = (componentName: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'start opslaan',
      label: componentName,
    })

  export const saveToNewCollection = (componentName: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'maak een nieuwe collectie',
      label: componentName,
    })

  export const saveToExistingCollection = (componentName: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'component opslaan',
      label: componentName,
    })

  export const cancelSaveToCollection = () =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'opslaan exit',
    })

    
  export const recalculateIngredientsUp = (
    multiplier: number
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'component detail',
      action: 'recalculate ingredients up',
      label: multiplier,
    })
    
  export const recalculateIngredientsDown = (
    multiplier: number
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'component detail',
      action: 'recalculate ingredients down',
      label: multiplier,
    })

  export const recalculateIngredientsPreset = (
    multiplier: number
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'component detail',
      action: 'recalculate ingredients preset',
      label: multiplier,
    })

  export const recalculateIngredientsText = (
    multiplier: number
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'component detail',
      action: 'recalculate ingredients text',
      label: multiplier,
    })

  export const goToComparableComponent = (componentName: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'vergelijkbare componenten',
      label: componentName,
    })

  export const saveComparableComponent = (componentName: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'vergelijkbare componenten',
      label: `${componentName} opslaan`,
    })

  export const successfulSaveToCollection = (componentName?: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'component succesvol opgeslagen',
      label: `${componentName} opslaan`,
    })

  export const goToCollection = (from: 'component' | 'dashboard') =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'collectie',
      action: 'opgeslagen collectie inzien',
      label: from,
    })

  // ==============
  // | Zoekpagina |
  // ==============

  export const noSearchResults = (searchCategory: string, searchTerm: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'zoeken',
      action: `geen resultaat ${searchCategory}`,
      label: searchTerm,
    })

  // =====================
  // | Product placement |
  // =====================

  export const loadSponsoredPage = (sponsorName: string) =>
    DatalayerApi.push({
      event: 'noninteraction',
      category: 'productplacement',
      action: sponsorName,
      label: '',
    })

  // ==================
  // | Product kennis |
  // ==================

  export const clickKnowledgeLetter = (knowledgeType: string, letter: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: knowledgeType,
      action: 'letter select',
      label: letter,
    })

  export const clickKnowledgeArticle = (
    knowledgeType: string,
    articleName: string
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: knowledgeType,
      label: articleName,
    })

  // =====================
  // | Seasonal calendar |
  // =====================

  export const filterSeasonCalendar = (month: string) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'seizoenskalender',
      action: 'filter op maand',
      label: month,
    })

  export const clickIngredientSeasonCalendar = (
    category: string,
    subcategory: string,
    ingredient: string
  ) =>
    DatalayerApi.push({
      event: 'interaction',
      category: 'seizoenskalender',
      action: `${category} - ${subcategory}`,
      label: ingredient,
    })

  // ==================
  // | InternalSearch |
  // ==================
  export const internalSearch = (
    term: string,
    category: string,
    language: string,
    referralPage: string
  ) => 
    DatalayerApi.push({
      event: 'interaction',
      category: 'InternalSearch',
      action: 'Search',
      searchTerm: term,
      searchCategory: category,
      language: language,
      referralPage: referralPage
    })

  export namespace DatalayerApi {
    interface DataLayerApi {
      push: (data: {}) => void
    }

    export const getDatalayer: () => Option<DataLayerApi> = () =>
      (window as any)['dataLayer'] ? some((window as any)['dataLayer']) : none()

    export const push: (data: any) => void = (data) => {
      const datalayer = getDatalayer()
      if (datalayer.kind == 'r') {
        console.log('pushed to dataLayer: ', data)
        datalayer.v.push(data)
      } else {
        console.log('DataLayer not found, failed to push event', data)
      }
    }
  }
}
